import clsx from 'clsx'
import { motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'

import { Text } from '@components/ui-core/text/text'

import { useStep } from '../hooks/use-step'
import { type Agent, type fullAgents } from '../utils/cards'
import { AgentsActive } from '../utils/icons'
import { Step } from '../utils/steps'

import style from './agents.module.scss'

const AGENT_HEIGHT = 68
const AGENT_WIDTH = 92
const AGENT_GAP = 16
const AGENT_BENCH_PADDING = 12
const NOTCH_WIDTH = 32

const MotionOverrides = {
  squashing: {
    animate: { scaleX: 0.7, scaleY: 1.4 },
    transition: {
      scaleX: { duration: 0.08, ease: [0.25, 0.46, 0.45, 0.94] },
      scaleY: { duration: 0.08, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  },
  morphing: {
    animate: { scaleX: 0.7, scaleY: 1.2 },
    transition: {
      scaleX: { duration: 0.35, ease: [0.34, 1.56, 0.64, 1] },
      scaleY: { duration: 0.35, ease: [0.34, 1.56, 0.64, 1] },
    },
  },
  stretching: {
    animate: { scaleX: 1.05, scaleY: 0.98 },
    transition: {
      scaleX: { duration: 0.15, ease: [0.34, 1.56, 0.64, 1] },
      scaleY: { duration: 0.15, ease: [0.34, 1.56, 0.64, 1] },
    },
  },
  hidden: {
    animate: { scaleX: 1, scaleY: 1, opacity: 0 },
    transition: {
      scaleX: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
      scaleY: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
      opacity: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
    },
  },
}

const useSlamMotion = (step: Step) => {
  const [status, setStatus] = useState<'squashing' | 'morphing' | 'stretching' | 'hidden' | null>(
    null,
  )
  const bounceRef = useRef(false)

  useEffect(() => {
    if (step !== Step.GoTime && bounceRef.current) {
      bounceRef.current = false
      setStatus(null)
    }
  }, [step])

  useEffect(() => {
    if (step !== Step.GoTime) {
      setStatus(null)
      return
    }

    const timeoutIds = [
      setTimeout(() => {
        setStatus('squashing')
      }, 300),
      setTimeout(() => {
        setStatus('morphing')
      }, 350),
      setTimeout(() => {
        setStatus('stretching')
      }, 420),
      setTimeout(() => {
        setStatus('hidden')
      }, 550),
    ]

    return () => {
      for (const timeoutId of timeoutIds) {
        clearTimeout(timeoutId)
      }
    }
  }, [step])

  const { animate, transition } = status ? MotionOverrides[status] : {}
  return { animate, transition }
}

const Agents = ({ agents }: { agents: typeof fullAgents }) => {
  const { isLockedToGoTime, step } = useStep()
  const { animate, transition } = useSlamMotion(step)
  const moveToRight = step === Step.GoTime
  if (isLockedToGoTime) return null

  return (
    <motion.div
      className={clsx(
        style.agentBench,
        step === Step.AgentsGo && style.highlightGO,
        step === Step.Proofreader && style.highlightProofreader,
        step === Step.Plus && style.highlightPlus,
      )}
      initial={{
        opacity: 0,
        scale: 0.8,
        // initial make it a rounded
        height: Math.max(AGENT_WIDTH, AGENT_HEIGHT),
        padding: 0,
        gap: 0,
        x: 0,
        translateY: '-50%',
        width: AGENT_WIDTH,
        borderRadius: '34px',
      }}
      animate={{
        opacity: 1,
        scale: moveToRight ? 0.8 : 1,
        height: getAgentBenchHeight(agents.length),
        gap: '16px',
        padding: AGENT_BENCH_PADDING,
        width: moveToRight ? NOTCH_WIDTH : AGENT_WIDTH,
        // same value as right value of agentBench
        x: moveToRight ? 'calc(max((50dvw -  min(40rem, 1/3 * 100dvw)), 16rem))' : 0,
        translateY: '-50%',
        borderRadius: moveToRight ? '12px 0 0 12px' : '34px',
        ...animate,
      }}
      transition={{
        opacity: { duration: 0.7, ease: [0.4, 0, 0.2, 1] },
        scale: { duration: 0.7, ease: [0.4, 0, 0.6, 1] },
        height: {
          type: 'spring',
          stiffness: 280,
          damping: 25,
          duration: moveToRight ? 0.2 : 0.7,
        },
        x: { duration: 0.3, ease: [1, -0.04, 1, 1] },
        width: { duration: 0.3, ease: [1, -0.04, 1, 1] },
        borderRadius: { delay: moveToRight ? 0.3 : 0 },
        ...transition,
      }}
    >
      <AgentHighlightRing step={step} />
      {agents.map(agent => (
        <Agent agent={agent} key={`${agent.name}-${agents.length}`} step={step} />
      ))}
    </motion.div>
  )
}

const getAgentBenchHeight = (agentsLength: number) =>
  agentsLength * AGENT_HEIGHT + AGENT_GAP * (agentsLength - 1) + 2 * AGENT_BENCH_PADDING

const Agent = ({ agent, step }: { agent: Agent; step: Step }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{
      scale: { type: 'spring', stiffness: 200 },
      duration: step === Step.Plus ? 0.3 : 0.7,
      ease: [0.4, 0, 0.6, 1],
    }}
    className={style.agent}
  >
    <span className={style.agentIcon}>
      <agent.icon />
    </span>
    <Text as="span" className={style.agentName} data-name={agent.name}>
      {agent.hideName ? '' : agent.name}
    </Text>
  </motion.div>
)

const highlightYIndexMap: Record<Step, number> = {
  [Step.AgentsGo]: 0,
  [Step.Proofreader]: 1,
  [Step.Plus]: 6,
  [Step.GrammarlyOnly]: -1,
  [Step.FullBench]: -1,
  [Step.UseAgentSkillAnyWhere]: -1,
  [Step.GoTime]: -1,
}

const AgentHighlightRing = ({ step }: { step: Step }) => {
  const slowTransition = step === Step.Plus
  const highlightIndex = highlightYIndexMap[step]
  const isVisible = highlightIndex !== -1

  const nextAgentY = highlightIndex * (AGENT_HEIGHT + AGENT_GAP)

  return (
    <motion.div
      className={style.agentActive}
      initial={{ left: AGENT_BENCH_PADDING, top: AGENT_BENCH_PADDING, opacity: 0 }}
      animate={{
        y: nextAgentY,
        opacity: isVisible ? 1 : 0,
      }}
      transition={{
        y: slowTransition
          ? { type: 'spring', bounce: 0.2, duration: 1 }
          : { type: 'spring', stiffness: 400, damping: 30 },
        opacity: {
          duration: isVisible ? 0.3 : 0,
          delay: step === Step.Plus || step === Step.AgentsGo ? 0.1 : 0,
        },
      }}
    >
      <AgentsActive />
    </motion.div>
  )
}

export default Agents
